import { QDReportSender } from "@/components/QDReport";
import type { ReportDataProps } from "@/components/QDReport";
import React, { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import {
  addObserver,
  initObserver,
  removeObserver,
  EVENT_COMMON_EXPOSURE_CHANGE,
} from "@/components/IntersectionObserver/observer";
import noop from "lodash/noop";

interface TargetComponentProps {
  ref: any;
  children?: ReactNode;
}

interface IntersectionObserverProps {
  Component?: string | React.ComponentType<TargetComponentProps>;
  containerId?: string;
  children?: ReactNode;
  duration?: number;
  onShow?: Function;
  once?: boolean; // 只曝光一次
  report?: ReportDataProps | null;
  className?: string;
}
const IntersectionObserver = ({
  Component = "div",
  containerId, // 视窗元素的 id，不传则为顶级文档的视窗
  duration = 1000,
  onShow = noop,
  once = false,
  report = null,
  children,
  ...otherProps
}: IntersectionObserverProps) => {
  const ref = useRef<HTMLElement | null>();
  const callbackTimer = useRef<ReturnType<typeof setInterval>>();
  const hasShown = useRef<boolean | null>();

  const handleShow = () => {
    clearTimeout(callbackTimer.current);
    if (!once) {
      hasShown.current = true; // 重置 hasShown 以做到重复上报
    }
    callbackTimer.current = setTimeout(() => {
      onShow();
      if (report) {
        QDReportSender({
          etype: "C",
          ...report,
        });
      }
    }, duration);
  };
  const handleToggleShow = (e: CustomEvent) => {
    const show = e.detail.show;
    if (show && !hasShown.current) {
      hasShown.current = true; // 重置 hasShown 以做到重复上报
      handleShow();
    } else {
      clearTimeout(callbackTimer.current);
      hasShown.current = false; // 重置 hasShown 以做到重复上报
    }
  };
  useEffect(() => {
    initObserver(containerId);
    addObserver(containerId, ref.current);
    ref.current?.addEventListener(
      EVENT_COMMON_EXPOSURE_CHANGE,
      handleToggleShow as EventListener
    );
    return () => {
      removeObserver(containerId, ref.current);
      ref.current?.removeEventListener(
        EVENT_COMMON_EXPOSURE_CHANGE,
        handleToggleShow as EventListener
      );
      clearTimeout(callbackTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId]);

  return (
    <Component {...otherProps} ref={ref}>
      {children}
    </Component>
  );
};

export default IntersectionObserver;
